import { useCallback, useEffect, useMemo, useState } from "react";
import { SortableHeader, TableSortResetBar, sortRowsByKey, useTableSort } from "@/components/dataTable";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";
import { Spinner } from "@/components/ui/Spinner";
import { TextInput } from "@/components/ui/Field";
import { DataTableScrollShell } from "@/components/dataTable/DataTableScrollShell";
import { SelectInput } from "@/components/ui/Field";
import { getHeroProjectTypes, getHeroProjectSteps } from "@/lib/projects";
import {
  deleteHeroStepAction,
  getHeroStepActionMap,
  saveHeroStepActionMap,
  saveHeroStepActions,
  type HeroStepAction,
  type HeroStepMapping,
} from "../../lib/settingsApi";

export function HeroStepActionMatrix() {
  const [actions, setActions] = useState<HeroStepAction[]>([]);
  const [mappings, setMappings] = useState<Map<string, string>>(new Map());
  const [projectTypes, setProjectTypes] = useState<Array<{ id: number; name: string }>>([]);
  const [stepsByType, setStepsByType] = useState<Record<number, Array<{ id: number; name: string }>>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newActionKey, setNewActionKey] = useState("");
  const [newActionLabel, setNewActionLabel] = useState("");
  const { sort, toggle, reset } = useTableSort();
  const sortedActions = useMemo(
    () =>
      sortRowsByKey(actions, sort, (action, key) => {
        const actionKey = action.action_key || action.key || "";
        if (key === "label") return action.label || actionKey;
        if (key === "action_key") return actionKey;
        const typeId = Number(key.slice(3));
        const stepId = mappings.get(`${actionKey}|${typeId}`);
        return stepsByType[typeId]?.find((step) => String(step.id) === stepId)?.name || "";
      }),
    [actions, mappings, sort, stepsByType],
  );

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [cfg, types] = await Promise.all([getHeroStepActionMap(), getHeroProjectTypes()]);
      setActions(cfg.actions || []);
      const map = new Map<string, string>();
      (cfg.mappings || []).forEach((m) => {
        map.set(`${m.action_key}|${m.project_type_id}`, String(m.step_id));
      });
      setMappings(map);
      setProjectTypes(types || []);
      const steps: Record<number, Array<{ id: number; name: string }>> = {};
      for (const pt of types || []) {
        const data = await getHeroProjectSteps(pt.id);
        steps[pt.id] = (data || []).map((s) => ({ id: s.id, name: s.name }));
      }
      setStepsByType(steps);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Matrix konnte nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  function setMapping(actionKey: string, typeId: number, stepId: string) {
    setMappings((prev) => {
      const next = new Map(prev);
      const k = `${actionKey}|${typeId}`;
      if (stepId) next.set(k, stepId);
      else next.delete(k);
      return next;
    });
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      await saveHeroStepActions(
        actions.map((a) => ({
          action_key: a.action_key || a.key || "",
          label: a.label || a.action_key || "",
        })),
      );
      const items: HeroStepMapping[] = [];
      mappings.forEach((stepId, key) => {
        const [action_key, typeId] = key.split("|");
        if (action_key && typeId && stepId) {
          items.push({
            action_key,
            project_type_id: parseInt(typeId, 10),
            step_id: parseInt(stepId, 10),
          });
        }
      });
      await saveHeroStepActionMap(items);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  async function handleAddAction() {
    const key = newActionKey.trim();
    if (!key) return;
    setActions((prev) => [...prev, { action_key: key, label: newActionLabel.trim() || key }]);
    setNewActionKey("");
    setNewActionLabel("");
  }

  async function handleDeleteAction(actionKey: string) {
    if (!window.confirm(`Aktion „${actionKey}" wirklich löschen?`)) return;
    try {
      await deleteHeroStepAction(actionKey);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Löschen fehlgeschlagen.");
    }
  }

  return (
    <Card
      title="Hero – Projekt-Steps pro Aktion"
      description="Welcher Status-Schritt wird bei welcher Aktion gesetzt?"
      actions={
        <Button type="button" onClick={handleSave} loading={saving}>
          Speichern
        </Button>
      }
    >
      {loading && (
        <div className="flex items-center gap-2 text-sm text-label-secondary">
          <Spinner /> Lädt …
        </div>
      )}
      {error && <Alert variant="error" title="Fehler" className="mb-3">{error}</Alert>}

      <div className="mb-4 flex flex-wrap gap-2">
        <TextInput
          placeholder="Aktion-ID"
          value={newActionKey}
          onChange={(e) => setNewActionKey(e.target.value)}
        />
        <TextInput
          placeholder="Bezeichnung"
          value={newActionLabel}
          onChange={(e) => setNewActionLabel(e.target.value)}
        />
        <Button type="button" variant="secondary" onClick={handleAddAction}>
          Aktion hinzufügen
        </Button>
      </div>

      {!loading && (
        <>
        <TableSortResetBar visible={sort != null} onReset={reset} />
        <DataTableScrollShell
          className="overflow-x-auto"
          syncKey={`hero-steps|${actions.length}|${projectTypes.length}`}
          head={
            <tr>
              <th className="eh-data-th px-3 py-2 text-left"><SortableHeader label="Aktion" columnKey="label" sort={sort} onSort={toggle} /></th>
              <th className="eh-data-th px-3 py-2 text-left"><SortableHeader label="Aktion-ID" columnKey="action_key" sort={sort} onSort={toggle} /></th>
              {projectTypes.map((pt) => (
                <th key={pt.id} className="eh-data-th px-3 py-2 text-left whitespace-nowrap">
                  <SortableHeader label={pt.name} columnKey={`pt_${pt.id}`} sort={sort} onSort={toggle} />
                </th>
              ))}
            </tr>
          }
        >
          <tbody className="divide-y divide-separator">
              {sortedActions.map((a) => {
                const actionKey = a.action_key || a.key || "";
                return (
                  <tr key={actionKey}>
                    <td className="px-3 py-2 align-top">
                      <div className="flex items-center gap-2">
                        <strong>{a.label || actionKey}</strong>
                        <Button
                          type="button"
                          size="sm"
                          variant="danger"
                          onClick={() => handleDeleteAction(actionKey)}
                        >
                          Löschen
                        </Button>
                      </div>
                    </td>
                    <td className="px-3 py-2 align-top text-label-secondary">{actionKey}</td>
                    {projectTypes.map((pt) => (
                      <td key={pt.id} className="px-3 py-2 align-top min-w-[160px]">
                        <SelectInput
                          value={mappings.get(`${actionKey}|${pt.id}`) || ""}
                          onChange={(e) => setMapping(actionKey, pt.id, e.target.value)}
                        >
                          <option value="">— nichts ändern —</option>
                          {(stepsByType[pt.id] || []).map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.name}
                            </option>
                          ))}
                        </SelectInput>
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
        </DataTableScrollShell>
        </>
      )}
    </Card>
  );
}
